import React, { useState, useEffect } from 'react';
import { checkLeaderExists, subscribeLeaderInfo, subscribeTransactions, subscribeGiftHistory } from './lib/backend';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, ArrowRight, TrendingUp, TrendingDown, CheckCircle, Circle, ShieldQuestion, Loader, Gift, Wallet } from 'lucide-react';
import PWAInstallPrompt from './PWAInstallPrompt';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US').format(Math.abs(amount));

// --- State Management Helpers ---
const getSeenTransactions = () => {
    const seen = localStorage.getItem('seenTransactions');
    return seen ? JSON.parse(seen) : {};
};

const toggleSeenTransaction = (txId) => {
    const seen = getSeenTransactions();
    if (seen[txId]) delete seen[txId];
    else seen[txId] = true;
    localStorage.setItem('seenTransactions', JSON.stringify(seen));
};

// ============================================================================
// --- 1. Login Screen ---
// ============================================================================
const LoginScreen = ({ onLoginSuccess }) => {
    const [leaderId, setLeaderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!leaderId.trim()) return;
        setLoading(true);
        setError('');

        try {
            const exists = await checkLeaderExists(leaderId.trim());
            if (exists) onLoginSuccess(leaderId.trim());
            else setError('المعرّف غير صحيح. يرجى التأكد والمحاولة مرة أخرى.');
        } catch (err) {
            setError('حدث خطأ أثناء التحقق. يرجى المحاولة لاحقاً.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            dir="rtl"
            className="min-h-screen bg-slate-950 text-white flex flex-col justify-center items-center p-4 relative overflow-hidden"
        >
            {/* Incredible Background Effects */}
            <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-teal-600/30 rounded-full blur-[120px] mix-blend-screen pointer-events-none" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] mix-blend-screen pointer-events-none" />

            <div className="w-full max-w-sm bg-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] z-10">
                <motion.div 
                    initial={{ y: -20, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.2 }}
                    className="text-center mb-8"
                >
                    <div className="mx-auto h-20 w-20 bg-gradient-to-br from-teal-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-500/30 mb-6 transform rotate-3">
                        <ShieldQuestion className="h-10 w-10 text-white transform -rotate-3" />
                    </div>
                    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-200 to-teal-500 mb-2">بوابة القائد</h1>
                    <p className="text-slate-400">سجل الدخول للمتابعة</p>
                </motion.div>

                <form onSubmit={handleLogin}>
                    <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="mb-6">
                        <input
                            type="text"
                            value={leaderId}
                            onChange={(e) => setLeaderId(e.target.value)}
                            className="bg-black/20 border border-white/10 rounded-xl w-full py-4 px-4 text-white text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-black/40 transition-all font-mono text-lg"
                            placeholder="ID-123..."
                            required
                        />
                    </motion.div>
                    <AnimatePresence>
                        {error && (
                            <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="text-rose-400 text-sm text-center mb-6">
                                {error}
                            </motion.p>
                        )}
                    </AnimatePresence>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold py-4 px-4 rounded-xl shadow-lg shadow-teal-500/25 transition-all disabled:opacity-50"
                    >
                        {loading ? <Loader className="animate-spin" /> : 'تسجيل الدخول'}
                        {!loading && <ArrowRight size={20} />}
                    </motion.button>
                </form>
            </div>
        </motion.div>
    );
};

// ============================================================================
// --- 2. Advanced Transaction Item ---
// ============================================================================
const TransactionItem = ({ transaction, isSeen, onToggleSeen, isGiftSection }) => {
    let isDeposit = false;
    let transactionTitle = '';

    if (isGiftSection) {
        isDeposit = transaction.type === 'add';
        transactionTitle = transaction.note || (isDeposit ? 'إضافة هدية' : 'صرف هدية');
    } else {
        isDeposit = transaction.type === 'deposit';
        transactionTitle = isDeposit ? (transaction.depositorName || 'إيداع') : 'رفع';
    }

    return (
        <motion.li
            variants={{
                hidden: { opacity: 0, y: 30, scale: 0.95 },
                visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 24 } }
            }}
            whileHover={{ scale: 1.01 }}
            className={`relative overflow-hidden flex items-center justify-between p-4 rounded-2xl transition-colors duration-300 border border-white/5 ${
                isSeen ? 'bg-white/5 opacity-70' : 'bg-white/10 shadow-md'
            }`}
        >
            {/* Color Accent Indicator */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isDeposit ? 'bg-gradient-to-b from-green-400 to-emerald-600' : 'bg-gradient-to-b from-rose-400 to-red-600'}`} />

            <div className="flex items-center gap-3 flex-1 min-w-0 ml-2">
                <div className={`shrink-0 p-3 rounded-2xl shadow-inner ${isDeposit ? 'bg-green-500/20 text-green-400' : 'bg-rose-500/20 text-rose-400'}`}>
                    {isDeposit ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={`text-base sm:text-lg font-bold truncate ${isSeen ? 'text-slate-400 line-through' : 'text-white'}`}>
                        {transactionTitle}
                    </h3>
                    <p className="text-xs text-slate-400 truncate">
                        {new Date(transaction.timestamp).toLocaleString('ar-IQ', { dateStyle: 'long', timeStyle: 'short' })}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
                <p className={`font-black text-xl tracking-tight ${isSeen ? 'text-slate-500' : (isDeposit ? 'text-green-400' : 'text-rose-400')}`}>
                    {isDeposit ? '+' : '-'}{formatCurrency(transaction.amount)}
                </p>
                <motion.button 
                    whileTap={{ scale: 0.8 }}
                    onClick={() => onToggleSeen(transaction.id)} 
                    className="text-slate-500 hover:text-white transition-colors"
                >
                    {isSeen ? <CheckCircle size={28} className={isGiftSection ? 'text-amber-400' : 'text-teal-400'} /> : <Circle size={28} />}
                </motion.button>
            </div>
        </motion.li>
    );
};

// ============================================================================
// --- 3. Leader Portal ---
// ============================================================================
const LeaderPortal = ({ leaderId, onLogout }) => {
    const [leaderName, setLeaderName] = useState('');
    const [balance, setBalance] = useState(0);
    const [giftBalance, setGiftBalance] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seenTxs, setSeenTxs] = useState(getSeenTransactions());
    const [activeTab, setActiveTab] = useState('balance');

    useEffect(() => {
        const unsubMe = subscribeLeaderInfo(leaderId, (me) => {
            setLeaderName(me.name || '...');
            setBalance(me.balance || 0);
            setGiftBalance(me.gift || 0);
        });

        return () => { unsubMe(); };
    }, [leaderId]);

    useEffect(() => {
        setLoading(true);
        
        const subscribe = activeTab === 'balance' ? subscribeTransactions : subscribeGiftHistory;
        const unsub = subscribe(leaderId, (loaded) => {
            setTransactions(loaded);
            setLoading(false);
        });

        return () => unsub();
    }, [leaderId, activeTab]);

    const displayBalance = activeTab === 'balance' ? balance : giftBalance;
    const isGift = activeTab === 'gift';

    let balanceColorClass = 'text-transparent bg-clip-text bg-gradient-to-br from-teal-200 to-emerald-500';
    let balancePrefix = '';
    let glowTopClass = 'bg-emerald-400';
    let glowBottomClass = 'bg-teal-600';
    
    if (activeTab === 'balance') {
        if (displayBalance > 0) {
            balanceColorClass = 'text-transparent bg-clip-text bg-gradient-to-br from-teal-400 to-emerald-500';
            balancePrefix = '+';
            glowTopClass = 'bg-emerald-400';
            glowBottomClass = 'bg-teal-600';
        } else if (displayBalance < 0) {
            balanceColorClass = 'text-transparent bg-clip-text bg-gradient-to-br from-rose-400 to-red-600';
            balancePrefix = '-';
            glowTopClass = 'bg-rose-500';
            glowBottomClass = 'bg-red-700';
        } else {
            balanceColorClass = 'text-slate-100';
            balancePrefix = '';
            glowTopClass = 'bg-slate-400';
            glowBottomClass = 'bg-slate-600';
        }
    } else {
        balanceColorClass = 'text-transparent bg-clip-text bg-gradient-to-br from-amber-200 to-orange-500';
        balancePrefix = displayBalance > 0 ? '+' : '';
        glowTopClass = 'bg-amber-400';
        glowBottomClass = 'bg-orange-600';
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            dir="rtl"
            className="min-h-screen pb-20 bg-slate-950 text-slate-100 p-4 sm:p-6 overflow-hidden relative"
        >
            {/* Dynamic Background Glow representing the active tab */}
            <motion.div 
                animate={{ backgroundColor: isGift ? 'rgba(245, 158, 11, 0.1)' : 'rgba(20, 184, 166, 0.1)' }}
                className="absolute top-0 left-0 right-0 h-96 transition-colors duration-1000 -z-10 rounded-b-[100%] blur-[60px]"
            />

            <div className="container mx-auto max-w-2xl relative z-10">
                {/* Header */}
                <header className="flex justify-between items-center mb-8 pt-4">
                    <div>
                        <p className="text-slate-400 text-sm font-medium mb-1">مرحباً بعودتك،</p>
                        <h1 className="text-3xl font-black text-white tracking-tight">{leaderName}</h1>
                    </div>
                    <motion.button 
                        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                        onClick={onLogout} 
                        className="bg-white/5 border border-white/10 hover:bg-rose-500/20 hover:text-rose-400 text-slate-300 py-2.5 px-4 rounded-xl transition-all flex items-center gap-2 font-bold"
                    >
                        <LogOut size={18} /> خروج
                    </motion.button>
                </header>

                {/* Liquid Tabs using layoutId */}
                <div className="flex bg-white/5 backdrop-blur-xl rounded-2xl p-1.5 mb-8 border border-white/10 shadow-xl relative">
                    {['balance', 'gift'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`relative flex-1 flex justify-center items-center gap-2 py-3.5 rounded-xl font-bold text-lg z-10 transition-colors ${activeTab === tab ? 'text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            {activeTab === tab && (
                                <motion.div 
                                    layoutId="activeTabBackground"
                                    className={`absolute inset-0 rounded-xl -z-10 ${tab === 'balance' ? 'bg-gradient-to-r from-teal-500 to-emerald-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                />
                            )}
                            {tab === 'balance' ? <Wallet size={20} /> : <Gift size={20} />}
                            {tab === 'balance' ? 'الرصيد الأساسي' : 'رصيد الهدايا'}
                        </button>
                    ))}
                </div>

                {/* Massive Animated Balance Card */}
                <AnimatePresence mode="popLayout">
                    <motion.div 
                        key={activeTab}
                        initial={{ scale: 0.9, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: -10 }}
                        transition={{ type: "spring", stiffness: 250, damping: 20 }}
                        className="bg-white/5 backdrop-blur-xl p-8 rounded-[2rem] shadow-xl border border-white/10 text-center mb-10 overflow-hidden relative"
                    >
                        <div className={`absolute top-0 right-0 w-24 h-24 blur-2xl opacity-40 ${glowTopClass}`} />
                        <div className={`absolute bottom-0 left-0 w-24 h-24 blur-2xl opacity-40 ${glowBottomClass}`} />

                        <p className="text-slate-300 font-medium mb-3 text-base relative z-10">
                            {isGift ? 'إجمالي الهدايا المكتسبة' : 'الرصيد المتاح'}
                        </p>
                        
                        <motion.p 
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
                            className={`text-4xl sm:text-6xl font-black tracking-tight drop-shadow-lg relative z-10 truncate px-2 ${balanceColorClass}`}
                        >
                            {balancePrefix}{formatCurrency(displayBalance)}
                        </motion.p>
                    </motion.div>
                </AnimatePresence>

                {/* Staggered Animated List */}
                <div className="flex justify-between items-center mb-6 px-2">
                    <h2 className="text-2xl font-extrabold text-white">سجل العمليات</h2>
                    <span className="bg-white/10 text-xs px-3 py-1.5 rounded-full font-bold">آخر 100</span>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader className={`animate-spin w-12 h-12 ${isGift ? 'text-amber-500' : 'text-teal-500'}`} />
                    </div>
                ) : (
                    <AnimatePresence>
                        {transactions.length > 0 ? (
                            <motion.ul
                                initial="hidden"
                                animate="visible"
                                variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
                                className="space-y-4"
                            >
                                {transactions.map((tx) => (
                                    <TransactionItem 
                                        key={tx.id} 
                                        transaction={tx} 
                                        isSeen={!!seenTxs[tx.id]}
                                        onToggleSeen={(id) => {
                                            toggleSeenTransaction(id);
                                            setSeenTxs(getSeenTransactions());
                                        }}
                                        isGiftSection={isGift}
                                    />
                                ))}
                            </motion.ul>
                        ) : (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16 bg-white/5 border border-white/5 rounded-3xl backdrop-blur-sm">
                                <p className="text-slate-400 text-lg">لا توجد عمليات مسجلة حتى الآن.</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}
            </div>
        </motion.div>
    );
};

// ============================================================================
// --- Main Entry ---
// ============================================================================
export default function App() {
    const [leaderId, setLeaderId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLeaderId(localStorage.getItem('leaderId'));
        setLoading(false);
    }, []);

    if (loading) return <div className="min-h-screen bg-slate-950" />;

    return (
        <>
            <PWAInstallPrompt />
            <AnimatePresence mode="wait">
                {leaderId ? (
                    <LeaderPortal key="portal" leaderId={leaderId} onLogout={() => { localStorage.removeItem('leaderId'); setLeaderId(null); }} />
                ) : (
                    <LoginScreen key="login" onLoginSuccess={(id) => { localStorage.setItem('leaderId', id); setLeaderId(id); }} />
                )}
            </AnimatePresence>
        </>
    );
}
